import { useEffect, useMemo, useRef, useState } from 'react';
import { useReducedMotion } from '../tacticsHud';

/**
 * One movement arrow on the 2D pitch.
 *
 * Mirrors the 3D `MovementArrow3D` (TubeGeometry along a Catmull-Rom
 * curve). Here we render an SVG `<path>`. For ≥ 3 control points we
 * convert the polyline into a smoothed cubic Bézier path using the
 * standard Catmull-Rom-to-Bézier formulation. For 2 control points
 * the arrow is just a straight line — same as the 3D version's tube
 * over a 2-point curve.
 *
 * Coordinates are stored in world metres so layouts stay portable
 * between 2D and 3D (same `tactics-3d-${id}` persistence). The
 * landscape 2D viewBox maps SVG-x = -world.z and SVG-y = world.x;
 * the path builder below applies that swap at the render layer.
 */
export interface MovementArrow2DSpec {
  id: string;
  /** Sequence of {x, z} world coordinates the arrow passes through. */
  points: Array<{ x: number; z: number }>;
  /** CSS hex colour. */
  color: string;
  /** Optional player binding ("home-3" / "away-7"). */
  playerKey?: string;
}

interface Props {
  arrow: MovementArrow2DSpec;
  /** When true (run-path playback active), don't animate the dash. */
  reduced?: boolean;
}

/**
 * Build an SVG `d` attribute that smooths a polyline of ≥3 points
 * using cubic Béziers derived from Catmull-Rom tangents. Tension 0.5
 * matches the visual feel of the 3D tube (centripetal Catmull-Rom
 * with a 0.4 alpha) closely enough for a 2D rendering at this scale.
 *
 * 2-point input falls through to a straight `M..L..` so we don't burn
 * cycles fitting a curve to a line.
 */
export function buildArrow2DPath(points: Array<{ x: number; z: number }>): string {
  if (points.length < 2) return '';
  const p = points;
  // World (x, z) → SVG (-z, x). Applied at every coordinate emission
  // below so the curve renders on the landscape pitch.
  if (p.length === 2) {
    return `M${-p[0].z},${p[0].x} L${-p[1].z},${p[1].x}`;
  }
  const tension = 0.5;
  let d = `M${-p[0].z},${p[0].x}`;
  for (let i = 0; i < p.length - 1; i++) {
    const p0 = p[i - 1] ?? p[i];
    const p1 = p[i];
    const p2 = p[i + 1];
    const p3 = p[i + 2] ?? p2;
    // Catmull-Rom tangents in world space, then mapped to SVG axes.
    const c1WorldX = p1.x + ((p2.x - p0.x) / 6) * tension * 2;
    const c1WorldZ = p1.z + ((p2.z - p0.z) / 6) * tension * 2;
    const c2WorldX = p2.x - ((p3.x - p1.x) / 6) * tension * 2;
    const c2WorldZ = p2.z - ((p3.z - p1.z) / 6) * tension * 2;
    const c1x = -c1WorldZ;
    const c1y = c1WorldX;
    const c2x = -c2WorldZ;
    const c2y = c2WorldX;
    d += ` C${c1x},${c1y} ${c2x},${c2y} ${-p2.z},${p2.x}`;
  }
  return d;
}

/** One-shot draw-on duration in milliseconds. When a new arrow mounts,
 *  its path renders progressively over this window before the regular
 *  marching-ants treatment takes over. Sky-MNF / Hawk-Eye telestrator
 *  feel — every annotation arrives with a stroke-in cue rather than
 *  popping in instantly. */
const DRAW_ON_MS = 350;

export function MovementArrow2D({ arrow, reduced = false }: Props) {
  const d = useMemo(() => buildArrow2DPath(arrow.points), [arrow.points]);
  const pathRef = useRef<SVGPathElement>(null);
  const [drawOnDone, setDrawOnDone] = useState(false);
  const reducedMotion = useReducedMotion();

  // Animate the stroke-dashoffset from full path length → 0 on mount
  // so the arrow appears to draw itself in. Once done we clear the
  // inline overrides so the regular dashed-marching-ants pattern (the
  // `<animate>` below) resumes for emphasis.
  useEffect(() => {
    if (reducedMotion || drawOnDone) return;
    const path = pathRef.current;
    if (!path) return;
    let totalLen = 0;
    try {
      totalLen = path.getTotalLength();
    } catch {
      // happy-dom / older Safari may not implement getTotalLength;
      // skip the animation rather than crash.
      setDrawOnDone(true);
      return;
    }
    if (!Number.isFinite(totalLen) || totalLen <= 0) {
      setDrawOnDone(true);
      return;
    }
    // Render fully hidden first frame, then transition to fully drawn.
    path.style.strokeDasharray = `${totalLen}`;
    path.style.strokeDashoffset = `${totalLen}`;
    path.style.transition = 'none';
    // Force a layout flush so the from-state pins before the
    // transition kicks in.
    void path.getBoundingClientRect();
    path.style.transition = `stroke-dashoffset ${DRAW_ON_MS}ms ease-out`;
    path.style.strokeDashoffset = '0';
    const timer = setTimeout(() => {
      path.style.strokeDasharray = '';
      path.style.strokeDashoffset = '';
      path.style.transition = '';
      setDrawOnDone(true);
    }, DRAW_ON_MS + 50);
    return () => clearTimeout(timer);
    // Re-run when the path geometry changes — a new arrow id mounts
    // a fresh component instance, but a coach reshaping an existing
    // arrow would otherwise miss the cue.
  }, [d, reducedMotion, drawOnDone]);

  if (!d) return null;
  return (
    <g
      data-testid={`t2d-arrow-${arrow.id}`}
      data-bound={arrow.playerKey ? 'true' : undefined}
      style={{ color: arrow.color }}
    >
      <path
        ref={pathRef}
        d={d}
        fill="none"
        stroke={arrow.color}
        strokeWidth={0.45}
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeDasharray="1.4 0.9"
        markerEnd="url(#t2d-ah)"
        opacity={0.92}
      >
        {!reduced && drawOnDone && (
          <animate
            attributeName="stroke-dashoffset"
            from="0"
            to="-4.6"
            dur="1.6s"
            repeatCount="indefinite"
          />
        )}
      </path>
    </g>
  );
}
